import { InfoOutlined, TaskOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { Box, Button, ListItem, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import SidebarNavItem from './SidebarNavItem';
import type { FC, ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import { get as getByPath } from 'lodash-es';

export const DOC_LINK = 'https://welltech.atlassian.net/wiki/spaces/MA1/pages/4248993927/WellSpy';
export const DOC_WIMAS = 'https://welltech.atlassian.net/wiki/spaces/MA1/pages/5120098319/WIMAS+Our+New+Incident+Management+System';

interface SupportItemConfig {
  icon: ReactNode;
  title: string;
  subtitle: string;
  subtitleColor: 'primary.main' | 'error.main';
  url: string;
}

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.action.active,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  gap: theme.spacing(1),
  display: 'flex',
  justifyContent: 'flex-start',
  flexGrow: 1,
  textTransform: 'none',
  textAlign: 'left',
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.primary,
  fontWeight: 400,
}));

const StyledSubtitle = styled(Typography)<{ subtitleColor: string }>(({ theme, subtitleColor }) => ({
  fontSize: theme.typography.caption.fontSize,
  color: getByPath(theme.palette, subtitleColor),
}));

interface SupportItemProps {
  config: SupportItemConfig;
  onItemClick: (url: string) => void;
}

const SupportItem: FC<SupportItemProps> = ({ config, onItemClick }) => {
  const handleClick = useCallback(() => {
    onItemClick(config.url);
  }, [config.url, onItemClick]);

  return (
    <ListItem>
      <StyledButton onClick={handleClick}>
        {config.icon}
        <Box>
          <StyledTitle>
            {config.title}
          </StyledTitle>
          <StyledSubtitle subtitleColor={config.subtitleColor}>
            {config.subtitle}
          </StyledSubtitle>
        </Box>
      </StyledButton>
    </ListItem>
  );
};

const SidebarNavSupport: FC = () => {
  const openLink = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const supportItems = useRef<SupportItemConfig[]>([
    {
      icon: <TaskOutlined sx={{ color: 'action.active', opacity: 0.56 }} fontSize="medium" />,
      title: 'Documentation',
      subtitle: 'Before reporting in Slack',
      subtitleColor: 'primary.main',
      url: DOC_LINK,
    },
    {
      icon: <WarningAmberOutlined sx={{ color: 'action.active', opacity: 0.56 }} fontSize="medium" />,
      title: 'Report in WIMAS',
      subtitle: 'For critical issues',
      subtitleColor: 'error.main',
      url: DOC_WIMAS,
    },
  ]).current;

  return (
    <SidebarNavItem icon={<InfoOutlined />} title="Support">
      {supportItems.map((item, index) => (
        <SupportItem
          key={index}
          config={item}
          onItemClick={openLink}
        />
      ))}
    </SidebarNavItem>
  );
};

export default SidebarNavSupport;
